import { oc } from "@orpc/contract";
import { z } from "zod";

// oxlint-disable-next-line no-control-regex -- intentional: rejects control characters in column names
const CONTROL_CHARS_RE = new RegExp("[\\u0000-\\u001f\\u007f]");

// Legacy v1 tables predate the SQL-safe identifier rules above — their column
// names (spaces, hyphens, up to 100 chars) must still round-trip through read
// responses. The request contract also keeps these established constraints;
// the service applies the stricter SQL identifier validation with a normal
// validation error so this additive storage migration is not an OpenAPI break.
const LegacyColumnNameSchema = z
  .string()
  .min(1)
  .max(100)
  .refine((v) => !CONTROL_CHARS_RE.test(v), {
    message: "Column names must not contain control characters",
  });

const ColumnTypeSchema = z.enum([
  "string",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
]);

/**
 * A stateful-table column as accepted on input. Exported because data mappings
 * describe the same columns — one vocabulary, one schema.
 */
export const StatefulColumnDefinitionSchema = z.object({
  name: LegacyColumnNameSchema,
  type: ColumnTypeSchema,
  description: z.string().min(10).max(500),
});

/**
 * The same column as read back. Deliberately relaxed on `description`: version
 * blobs predating the 10-character minimum (and legacy tables copied forward on
 * SQL-column validation alone) would otherwise fail output validation and make
 * the read unusable for exactly the tables that need fixing.
 */
export const StatefulStoredColumnSchema = StatefulColumnDefinitionSchema.extend(
  { description: z.string().max(500) },
);

const ColumnDefinitionSchema = StatefulColumnDefinitionSchema;
const ColumnDefinitionOutputSchema = StatefulStoredColumnSchema;

const ValidationErrorSchema = z.object({
  row_index: z.number().int().min(-1),
  column: z.string(),
  value: z.string().nullable(),
  expected_type: z.string(),
  error: z.string(),
});

const IngestModeSchema = z.enum(["snapshot", "upsert"]);

const ResolvedFrameStatusSchema = z.enum([
  "auto_resolved",
  "needs_review",
  "partial",
  "reviewed",
]);

const ColumnMatchSourceSchema = z.enum(["cache", "llm", "unmatched"]);

const ColumnMatchSchema = z.object({
  schema_column: z.string(),
  input_column: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  source: ColumnMatchSourceSchema,
});

const VersionSchema = z.object({
  id: z.uuid(),
  tableDefinitionId: z.uuid(),
  versionNumber: z.number().int().min(1),
  parentVersionId: z.uuid().nullable(),
  mostRecentFrameId: z.uuid().nullable(),
  createdBy: z.uuid(),
  /** Key columns in key order; more than one is a composite key. */
  keyColumns: z.array(z.string()).min(1),
  columns: z.array(ColumnDefinitionOutputSchema),
  createdAt: z.iso.datetime(),
  recastValidationErrors: z.array(ValidationErrorSchema),
  schemaDefinitionId: z.uuid().nullable(),
  schemaDefinitionVersionId: z.uuid().nullable(),
});

const DefinitionSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  createdBy: z.uuid(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  currentVersion: VersionSchema,
});

const FrameCellValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

const ResolvedFrameSchema = z.object({
  id: z.uuid(),
  rawFrameId: z.uuid(),
  frameNumber: z.number().int().min(1),
  versionId: z.uuid(),
  versionNumber: z.number().int().min(1),
  rowCount: z.number().int().min(0),
  columnCount: z.number().int().min(1),
  columns: z.array(z.string()),
  types: z.array(ColumnTypeSchema),
  rows: z.array(z.array(FrameCellValueSchema)),
  status: ResolvedFrameStatusSchema,
  columnMatches: z.array(ColumnMatchSchema),
  inputHeader: z.array(z.string()),
  validationErrors: z.array(ValidationErrorSchema),
  resolvedAt: z.iso.datetime(),
});

const RowDataSchema = z.record(z.string(), FrameCellValueSchema);

const LookupResultSchema = z.object({
  lookupKey: z.string(),
  rowData: RowDataSchema,
  frameNumber: z.number().int().min(1),
  versionNumber: z.number().int().min(1),
  resolvedAt: z.iso.datetime(),
});

const BatchLookupSchema = z.object({
  results: z.array(z.object({ lookupKey: z.string(), rowData: RowDataSchema })),
  notFound: z.array(z.string()),
  frameNumber: z.number().int().min(0),
  versionNumber: z.number().int().min(0),
  resolvedAt: z.iso.datetime(),
});

const IngestResultSchema = z.object({
  rawFrameId: z.uuid(),
  frameNumber: z.number().int().min(1),
  status: z.enum(["processed", "failed"]),
  mode: IngestModeSchema,
  resolvedFrameId: z.uuid().nullable(),
  resolvedFrameStatus: ResolvedFrameStatusSchema.nullable(),
  rowCount: z.number().int().min(0).nullable(),
  columnCount: z.number().int().min(1).nullable(),
  columnMatches: z.array(ColumnMatchSchema),
  validationErrors: z.array(ValidationErrorSchema),
  failureReason: z.string().nullable(),
});

/** Rows one row write accepts; larger loads go through a frame ingest. */
export const MAX_STATEFUL_ROW_WRITE_ROWS = 500;

/**
 * Rows written into a stateful table by key, with patch semantics: every row
 * names each key column, and only the columns a row names are written. An
 * existing row keeps the columns a write leaves out; a new row gets null.
 */
export const StatefulRowWriteSchema = z.object({
  tableId: z.uuid().describe("The stateful table's id."),
  rows: z
    .array(
      z
        .record(z.string().min(1), FrameCellValueSchema)
        .describe(
          "Column name to value. Include every key column; omit a column to leave it unchanged. Dates as YYYY-MM-DD, null clears a value.",
        ),
    )
    .min(1)
    .max(MAX_STATEFUL_ROW_WRITE_ROWS),
});
export type StatefulRowWrite = z.infer<typeof StatefulRowWriteSchema>;

export const StatefulRowWriteResultSchema = z.object({
  /** Rows inserted or updated, one per distinct key. */
  written: z.number().int().min(0),
  /** Values that could not be cast; their rows were skipped. */
  errors: z.array(
    z.object({
      rowIndex: z.number().int().min(0),
      column: z.string(),
      message: z.string(),
    }),
  ),
});
export type StatefulRowWriteResult = z.infer<
  typeof StatefulRowWriteResultSchema
>;

const FrameHistoryEntrySchema = z.object({
  rawFrameId: z.uuid(),
  frameNumber: z.number().int().min(1),
  status: z.enum(["pending", "processed", "failed"]),
  resolvedFrameStatus: ResolvedFrameStatusSchema.nullable(),
  mode: IngestModeSchema,
  rowCount: z.number().int().min(0).nullable(),
  columnCount: z.number().int().min(1).nullable(),
  // Null for frames written outside a workflow run — a data mapping pushing
  // its source file, for instance.
  workflowRunId: z.uuid().nullable(),
  resolvedFrameId: z.uuid().nullable(),
  validationErrorCount: z.number().int().min(0),
  createdAt: z.iso.datetime(),
});

export const statefulTablesContracts = {
  list: oc
    .input(
      z.object({
        page: z.number().int().min(1).optional(),
        perPage: z.number().int().min(1).max(100).optional(),
        name: z
          .string()
          .max(255)
          .optional()
          .describe("Only tables with this name, ignoring case."),
        search: z
          .string()
          .max(255)
          .optional()
          .describe("Only tables whose name contains this, ignoring case."),
      }),
    )
    .output(
      z.object({
        results: z.array(DefinitionSchema),
        page: z.number().int().min(1),
        perPage: z.number().int().min(1),
        total: z.number().int().min(0),
      }),
    )
    .route({
      summary: "List stateful tables",
      description:
        "Paginated list of the organization's stateful tables (versioned keyed tables that workflows write rows into), with their current column definitions.",
      method: "GET",
    }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ definition: DefinitionSchema }))
    .route({
      summary: "Get a stateful table",
      description:
        "Returns one stateful table's definition, including its current version, columns and key columns.",
      method: "GET",
    }),

  create: oc
    .input(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().max(2000).optional(),
        columns: z.array(ColumnDefinitionSchema).min(1).max(100),
        keyColumns: z.array(LegacyColumnNameSchema).min(1).max(8),
      }),
    )
    .output(z.object({ definition: DefinitionSchema }))
    .route({
      summary: "Create a stateful table",
      description:
        "Creates a keyed stateful table with a name, optional description, typed columns and up to eight key columns.",
      method: "POST",
    }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({
      summary: "Delete a stateful table",
      description:
        "Soft-deletes a stateful table definition so it no longer appears or serves lookups.",
      method: "DELETE",
    }),

  createVersion: oc
    .input(
      z.object({
        id: z.uuid(),
        columns: z.array(ColumnDefinitionSchema).min(1).max(100),
        keyColumns: z.array(LegacyColumnNameSchema).min(1).max(8),
      }),
    )
    .output(
      z.object({
        version: VersionSchema,
      }),
    )
    .route({
      summary: "Create a new stateful table version",
      description:
        "Defines a new column and key-column layout for an existing stateful table as its next version.",
      method: "POST",
    }),

  getLatestFrame: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ frame: ResolvedFrameSchema }))
    .route({
      summary: "Get a stateful table's latest data",
      description:
        "Returns the most recent resolved frame of a stateful table: columns, types, rows, review status and how input columns matched the schema.",
      method: "GET",
    }),

  listFrames: oc
    .input(
      z.object({
        id: z.uuid(),
        page: z.number().int().min(1).optional(),
        perPage: z.number().int().min(1).max(100).optional(),
      }),
    )
    .output(
      z.object({
        results: z.array(FrameHistoryEntrySchema),
        page: z.number().int().min(1),
        perPage: z.number().int().min(1),
        total: z.number().int().min(0),
      }),
    )
    .route({
      summary: "List a stateful table's ingest history",
      description:
        "Paginated history of data frames ingested into a stateful table, with status, mode, row and column counts, validation errors and originating workflow run.",
      method: "GET",
    }),

  lookupByKey: oc
    .input(z.object({ id: z.uuid(), key: z.string().min(1) }))
    .output(z.object({ lookup: LookupResultSchema }))
    .route({
      summary: "Look up a stateful table row by key",
      description:
        "Returns the row stored under one key in a stateful table's latest frame, with the frame and version it came from.",
      method: "GET",
    }),

  lookupByKeys: oc
    .input(
      z.object({
        id: z.uuid(),
        keys: z.array(z.string().min(1)).min(1).max(100),
      }),
    )
    .output(z.object({ result: BatchLookupSchema }))
    .route({
      summary: "Look up stateful table rows by keys",
      description:
        "Batch lookup of up to 100 keys in a stateful table's latest frame; returns found rows and the keys not found.",
      method: "POST",
    }),

  upsertRows: oc
    .input(StatefulRowWriteSchema)
    .output(StatefulRowWriteResultSchema)
    .route({
      summary: "Write rows into a stateful table by key",
      description:
        "Inserts or updates up to 500 rows by key. Each row must include every key column; only the columns a row includes are written, so an existing row keeps the others. Unknown columns or a missing key column reject the call; a value that cannot be cast skips its row and is listed in errors.",
      method: "POST",
    }),

  confirmMatches: oc
    .input(
      z.object({
        id: z.uuid(),
        frameNumber: z.number().int().min(1),
        matches: z
          .array(
            z.object({
              schema_column: z.string().min(1),
              input_column: z.string().nullable(),
            }),
          )
          .min(1),
      }),
    )
    .output(z.object({ frame: ResolvedFrameSchema }))
    .route({
      summary: "Confirm column matches for a table frame",
      description:
        "Saves reviewer-confirmed mappings from incoming file columns to table schema columns for one ingested frame and returns the re-resolved frame.",
      method: "POST",
    }),
} as const;

export type StatefulTableDefinitionResponse = z.infer<typeof DefinitionSchema>;
export const StatefulTableColumnSnapshotSchema =
  ColumnDefinitionOutputSchema.pick({
    name: true,
    type: true,
  });
export type StatefulTableColumnSnapshot = z.infer<
  typeof StatefulTableColumnSnapshotSchema
>;
export type StatefulTableVersionResponse = z.infer<typeof VersionSchema>;
export type StatefulTableFrameResponse = z.infer<typeof ResolvedFrameSchema>;
export type StatefulTableIngestResponse = z.infer<typeof IngestResultSchema>;
export type StatefulTableLookupResponse = z.infer<typeof LookupResultSchema>;
export type StatefulTableColumnMatch = z.infer<typeof ColumnMatchSchema>;
export type StatefulTableResolvedFrameStatus = z.infer<
  typeof ResolvedFrameStatusSchema
>;
export type StatefulTableFrameHistoryEntry = z.infer<
  typeof FrameHistoryEntrySchema
>;
